import React, { type ReactElement, useEffect } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import Banner from '@atlaskit/banner';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { Anchor, Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { assert, AssertionError, isServer } from '@atlassian/eoc-common';
import { useIsNewNavigation } from '@atlassian/eoc-global-context';
import { ResponderNamesWrapper } from '@atlassian/eoc-responder-names';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { SupportUrlKeys, type SupportUrls } from '../common/types';
import { HeartbeatsProvider, useHeartbeatsContext } from '../controllers/providers/heartbeats';
import { heartbeatsResource, heartbeatsUserRightsResource } from '../services/resources';

import { EmptyState } from './empty-state';
import { ErrorState } from './error-state';
import { Focus } from './focus';
import { Header as PageHeader } from './header';
import { ActionButtons } from './header/action-buttons';
import { BottomBar } from './header/bottom-bar';
import { Description } from './header/bottom-bar/description';
import { Filter } from './header/bottom-bar/filter';
import { List } from './list';
import messages from './messages';
import { ActionButtonsSkeleton, BottomBarSkeleton, ContentSkeleton } from './skeleton';

const ContainerStyles = xcss({
	minWidth: '1024px',
	paddingRight: 'space.300',
	paddingLeft: 'space.300',
});

const horizontalWrapperStyles = xcss({
	marginTop: 'space.300',
});

const Heartbeats = ({ breadcrumbs }: { breadcrumbs: ReactElement }) => {
	const { data, loading, error } = useResource(heartbeatsResource);
	const { loading: rightsLoading, error: rightsError } = useResource(heartbeatsUserRightsResource);
	const { formatMessage } = useIntl();
	const { supportUrls, isPrimaryAdmin } = useHeartbeatsContext();
	const opsgenieGlobalHeartbeatsUrl = supportUrls[SupportUrlKeys.OG_GLOBAL_HEARTBEATS];

	const isHorizontalNav = useIsNewNavigation();

	if (loading || rightsLoading || isServer()) {
		return (
			<UFOLoadHold name="eoc-heartbeats-loading">
				<Box xcss={ContainerStyles}>
					<PageHeader
						actionButtons={<ActionButtonsSkeleton />}
						bottomBar={<BottomBarSkeleton />}
						breadcrumbs={breadcrumbs}
					/>
					<ContentSkeleton />
				</Box>
			</UFOLoadHold>
		);
	}

	if (error || rightsError) {
		return (
			<Box xcss={ContainerStyles}>
				{isHorizontalNav ? (
					<Box xcss={horizontalWrapperStyles}>
						<Description />
					</Box>
				) : (
					<PageHeader bottomBar={<Description />} breadcrumbs={breadcrumbs} />
				)}
				<ErrorState />
			</Box>
		);
	}

	assert(!!data, new AssertionError('Heartbeats', 'component rendered before data is fetched!'));

	const { heartbeats, limit, limitExceeded } = data;

	if (!heartbeats.length) {
		return (
			<Box xcss={ContainerStyles}>
				{!isHorizontalNav && <PageHeader breadcrumbs={breadcrumbs} />}
				<EmptyState />
			</Box>
		);
	}
	const shouldDisplayLimitExceedBanner = limitExceeded && limit;

	return (
		<>
			{shouldDisplayLimitExceedBanner && (
				<Banner
					appearance="warning"
					icon={
						<WarningIcon
							color="currentColor"
							spacing="spacious"
							label=""
							LEGACY_secondaryColor="inherit"
						/>
					}
				>
					{formatMessage(messages.heartbeatLimitBanner, { limit })}
					{isPrimaryAdmin && opsgenieGlobalHeartbeatsUrl && (
						<Anchor href={opsgenieGlobalHeartbeatsUrl} target="_blank">
							{formatMessage(messages.viewInOgText)}
						</Anchor>
					)}
				</Banner>
			)}
			<Box xcss={ContainerStyles}>
				{isHorizontalNav ? (
					<Stack xcss={horizontalWrapperStyles} space="space.300">
						<Flex justifyContent="space-between">
							<Filter />
							<ActionButtons />
						</Flex>
						<Description />
					</Stack>
				) : (
					<PageHeader
						actionButtons={<ActionButtons />}
						bottomBar={<BottomBar />}
						breadcrumbs={breadcrumbs}
					/>
				)}

				<List />
			</Box>
		</>
	);
};

const HeartbeatsWrapper = React.memo(
	({
		isTransitioning,
		isPrimaryAdmin,
		supportUrls,
		breadcrumbs,
	}: {
		isTransitioning?: boolean;
		isPrimaryAdmin?: boolean;
		supportUrls: SupportUrls;
		breadcrumbs: ReactElement;
	}) => {
		const { createAnalyticsEvent } = useAnalyticsEvents();

		useEffect(() => {
			const time = Date.now();

			return () => {
				const current = Date.now();

				const event = createAnalyticsEvent({});
				fireTrackAnalytics(event, 'time consumed', {
					duration: current - time,
				});
			};
		}, [createAnalyticsEvent]);

		return (
			<ResponderNamesWrapper>
				<HeartbeatsProvider
					isTransitioning={isTransitioning}
					isPrimaryAdmin={isPrimaryAdmin}
					supportUrls={supportUrls}
				>
					<Heartbeats breadcrumbs={breadcrumbs} />
					<Focus />
				</HeartbeatsProvider>
			</ResponderNamesWrapper>
		);
	},
);

export default withAnalyticsContext({
	source: 'HeartbeatsPage',
	tags: ['heartbeats'],
})(HeartbeatsWrapper);
